import TriangleDown from "@redotech/redo-icons/play_down_filled.svg";
import TriangleRight from "@redotech/redo-icons/play_right_filled.svg";
import { memo, useState } from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../arbiter-components/checkbox/redo-checkbox";
import { Flex } from "../flex";
import { Text } from "../text";
import { ModalProduct, ModalVariants } from "./product-selection-modal";

export const UpsellModalProductRow = memo(function UpsellModalProductRow({
  product,
  selected,
  setSelected,
  exactlyOneVariant = false,
  multiselect = false,
  isAtSelectionLimit = false,
}: {
  product: ModalProduct;
  selected: {
    selectedProduct: ModalProduct;
    selectedVariants: ModalVariants[];
  }[];
  setSelected: (
    selected: {
      selectedProduct: ModalProduct;
      selectedVariants: ModalVariants[];
    }[],
  ) => void;
  exactlyOneVariant?: boolean;
  multiselect?: boolean;
  isAtSelectionLimit?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <Flex
        align="center"
        flex={1}
        gap="md"
        key={product.id}
        style={{ height: "32px" }}
      >
        <Flex align="center" flex={6}>
          <RedoCheckbox
            disabled={
              (multiselect
                ? false
                : selected.some((s) => s.selectedProduct.id !== product.id)) ||
              (isAtSelectionLimit &&
                !selected.some((s) => s.selectedProduct.id === product.id))
            }
            setValue={() => {
              if (selected.some((s) => s.selectedProduct.id === product.id)) {
                setSelected(
                  selected.filter((s) => s.selectedProduct.id !== product.id),
                );
              } else if (!isAtSelectionLimit) {
                setSelected([
                  ...selected,
                  {
                    selectedProduct: product,
                    selectedVariants: exactlyOneVariant
                      ? product.variants.slice(0, 1)
                      : product.variants,
                  },
                ]);
              }
            }}
            size="sm"
            value={selected.some((s) => s.selectedProduct.id === product.id)}
          />
          {product.image.src && (
            <img
              alt={product.image.altText || ""}
              height={32}
              src={product.image.src}
              width={32}
            />
          )}
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {product.title}
          </Text>
        </Flex>
        <Flex flex={2}>
          <Text
            fontSize="xs"
            fontWeight="regular"
            textColor="tertiary"
          >{`${product.variants.length} variants`}</Text>
        </Flex>
        <Flex flex={2} justify="flex-end">
          <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
            {`$${parseFloat(product.variants?.[0]?.price ?? "0").toFixed(2)}`}
          </Text>
        </Flex>
        <Flex flex={1} justify="flex-end">
          <RedoButton
            hierarchy="tertiary"
            IconLeading={expanded ? TriangleDown : TriangleRight}
            onClick={() => setExpanded(!expanded)}
            size="sm"
          />
        </Flex>
      </Flex>
      <Flex dir="column">
        {expanded &&
          product.variants.length > 1 &&
          product.variants.map((variant) => (
            <UpsellModalVariantRow
              exactlyOneVariant={exactlyOneVariant}
              isAtSelectionLimit={isAtSelectionLimit}
              key={variant.admin_graphql_api_id}
              product={product}
              selected={selected}
              setSelected={setSelected}
              variant={variant}
            />
          ))}
      </Flex>
    </>
  );
});

const UpsellModalVariantRow = memo(function UpsellModalVariantRow({
  variant,
  selected,
  setSelected,
  product,
  exactlyOneVariant = false,
  isAtSelectionLimit = false,
}: {
  variant: ModalVariants;
  selected: {
    selectedProduct: ModalProduct;
    selectedVariants: ModalVariants[];
  }[];
  setSelected: (
    selected: {
      selectedProduct: ModalProduct;
      selectedVariants: ModalVariants[];
    }[],
  ) => void;
  product: ModalProduct;
  exactlyOneVariant?: boolean;
  isAtSelectionLimit?: boolean;
}) {
  return (
    <Flex
      align="center"
      flex={1}
      gap="md"
      key={variant.admin_graphql_api_id}
      pl="xl"
      style={{ height: "32px" }}
    >
      <Flex align="center" flex={6}>
        <RedoCheckbox
          disabled={
            isAtSelectionLimit &&
            !selected.some((s) =>
              s.selectedVariants.some(
                (v) => v.admin_graphql_api_id === variant.admin_graphql_api_id,
              ),
            )
          }
          setValue={() => {
            setSelected(
              selected.map((s) => {
                if (s.selectedProduct.id === product.id) {
                  if (exactlyOneVariant) {
                    return { ...s, selectedVariants: [variant] };
                  }
                  if (
                    s.selectedVariants.some(
                      (v) =>
                        v.admin_graphql_api_id === variant.admin_graphql_api_id,
                    )
                  ) {
                    return {
                      ...s,
                      selectedVariants: s.selectedVariants.filter(
                        (v) =>
                          v.admin_graphql_api_id !==
                          variant.admin_graphql_api_id,
                      ),
                    };
                  }
                  if (!isAtSelectionLimit) {
                    return {
                      ...s,
                      selectedVariants: [...s.selectedVariants, variant],
                    };
                  }
                }
                return s;
              }),
            );
          }}
          size="sm"
          value={selected.some((s) =>
            s.selectedVariants.some(
              (v) => v.admin_graphql_api_id === variant.admin_graphql_api_id,
            ),
          )}
        />
        {variant.image.src && (
          <img
            alt={variant.image.altText || ""}
            height={32}
            src={variant.image.src}
            width={32}
          />
        )}
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {variant.title}
        </Text>
      </Flex>
      <Flex flex={2}>
        <Text
          fontSize="xs"
          fontWeight="regular"
          textColor="tertiary"
        >{`${variant.inventory_quantity} in stock`}</Text>
      </Flex>
      <Flex flex={2} justify="flex-end">
        <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
          {`$${parseFloat(variant.price).toFixed(2)}`}
        </Text>
      </Flex>
      <Flex flex={1} justify="flex-end" />
    </Flex>
  );
});
